import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import {
  sendUserAnswersAction,
  setAnswersAction,
} from 'root-redux/actions/user'
import { selectUUID } from 'root-redux/selects/user'

import { Events, eventLogger } from 'services/eventLogger.service'

import { PageId } from 'page-constants'
import { Language } from 'root-constants'

import { useRetentionFlowV2Config } from '../../hooks/useRetentionFlowV2Config'
import { FlowTitle } from '../../styles'
import { FlowButton } from '../FlowButton'
import { StyledFlowFeedbackPopup as S } from './FlowFeedbackPopup.styles'

const FEEDBACK_MAX_LENGTH = 1000
const FEEDBACK_POPUP_TITLE_ID = 'flow-feedback-popup-title'

type TProps = {
  isShown: boolean
  onComplete: () => void
  onClose: () => void
}

export const FlowFeedbackPopup: React.FC<TProps> = ({
  isShown,
  onComplete,
  onClose,
}) => {
  const { t } = useTranslation()
  const { copyKey } = useRetentionFlowV2Config()
  const dispatch = useDispatch()
  const userId = useSelector(selectUUID)

  const [feedback, setFeedback] = useState('')
  const isCompletedRef = useRef(false)

  const trimmedFeedback = feedback.trim()

  const complete = () => {
    if (isCompletedRef.current) return

    isCompletedRef.current = true
    onComplete()
  }

  const handleSend = () => {
    if (isCompletedRef.current) return

    dispatch(
      setAnswersAction({
        pageId: PageId.OTHER_ISSUES,
        answers: trimmedFeedback,
      }),
    )
    dispatch(sendUserAnswersAction())

    eventLogger.logQuestion({
      event: Events.UNSUB_FLOW_QUESTION_4_END,
      question: t(`${copyKey}.feedbackPopup.description`, {
        lng: Language.EN,
      }),
      answers: trimmedFeedback,
      userId,
    })

    complete()
  }

  if (!isShown) return null

  return (
    <S.Overlay>
      <S.Content
        role="dialog"
        aria-modal="true"
        aria-labelledby={FEEDBACK_POPUP_TITLE_ID}
      >
        <S.CloseButton
          type="button"
          aria-label={t(`${copyKey}.close`)}
          onClick={onClose}
        />
        <S.Headline>
          <FlowTitle id={FEEDBACK_POPUP_TITLE_ID}>
            {t(`${copyKey}.feedbackPopup.title`)}
          </FlowTitle>
          <S.Description>
            {t(`${copyKey}.feedbackPopup.description`)}
          </S.Description>
        </S.Headline>
        <S.Textarea
          value={feedback}
          placeholder={t(`${copyKey}.feedbackPopup.placeholder`)}
          maxLength={FEEDBACK_MAX_LENGTH}
          onChange={({ target: { value } }) => setFeedback(value)}
        />
        <S.Buttons>
          <FlowButton
            type="button"
            disabled={!trimmedFeedback}
            onClick={handleSend}
          >
            {t(`${copyKey}.feedbackPopup.send`)}
          </FlowButton>
          <FlowButton type="button" variant="transparent" onClick={complete}>
            {t(`${copyKey}.skip`)}
          </FlowButton>
        </S.Buttons>
      </S.Content>
    </S.Overlay>
  )
}
